import { ShipRoute } from '@/types/order'

import {
  ProductLineCoverageOption,
  ProductLinePackagingType,
  useProductLineApi,
} from '../api/useProductLineApi'
import useShipmentStore from '../stores/useShipmentStore'

export const INSURANCE_FIELD = 'insurance'
export const PACKAGING_FIELD = 'packaging'
export const DEFAULT_COVERAGE_OPTION_CENTS = 200000
export const DEFAULT_PACKAGING_TYPE_NAME = 'Not Specified'
export const BOX_NAME = 'Box'
export const INDIVIDUAL_CLUB_NAME = 'Individualclub'

type ProductLineOption = 'insurance' | 'packaging'

interface UseProductLineOptions {
  getOptions: <T>(productId: string, option: ProductLineOption) => T[]
  getCoverageOptions: (productId: string) => ProductLineCoverageOption[]
  getPackagingTypes: (productId: string) => ProductLinePackagingType[]
  getDefaultCoverageOption: (
    options: ProductLineCoverageOption[],
  ) => ProductLineCoverageOption | undefined
  getDefaultPackagingType: (
    options: ProductLinePackagingType[],
  ) => ProductLinePackagingType | undefined
  coverageOptions?: ProductLineCoverageOption[]
  packagingTypes?: ProductLinePackagingType[]
  isPending: boolean
  error?: Error
}

const isInternational = (shipRoute: ShipRoute): boolean => {
  const originCountry = shipRoute.origin?.countryCode?.toUpperCase()
  const destinationCountry = shipRoute.destination?.countryCode?.toUpperCase()
  if (!originCountry || !destinationCountry) return false
  const domestic = ['US:US', 'CA:CA']
  const key = `${originCountry}:${destinationCountry}`

  return domestic.indexOf(key) === -1
}

const useProductLineOptions = (): UseProductLineOptions => {
  const { shipRoute } = useShipmentStore((state) => state.shipment)
  const { initRoute } = useShipmentStore((state) => state)
  const { data: productLines, isLoading, error } = useProductLineApi(shipRoute || initRoute())

  const getOptions = <T>(productId: string, option: ProductLineOption): T[] => {
    if (!productLines) {
      return []
    }

    const productLine = productLines.find((pl) => pl.id === productId)
    if (!productLine) {
      return []
    }

    // TODO: add non_package field on the backend and filter there
    //       per @mhousden, hardcoding is a temporary solution
    // return no packaging types for Box and Individual Club
    if (
      option === PACKAGING_FIELD &&
      (productLine.name === BOX_NAME || productLine.name === INDIVIDUAL_CLUB_NAME)
    ) {
      return []
    }

    return (productLine[option] as T[]) || []
  }

  const coverageOptions = productLines?.flatMap((pl) => pl.insurance) || []
  const packagingTypes = productLines?.flatMap((pl) => pl.packaging) || []

  const getCoverageOptions: (productId: string) => ProductLineCoverageOption[] = (
    productId: string,
  ) => getOptions<ProductLineCoverageOption>(productId, INSURANCE_FIELD)

  const getDefaultCoverageOption: (
    options: ProductLineCoverageOption[],
  ) => ProductLineCoverageOption | undefined = (options) =>
    options.find(
      (option) =>
        option.defaultCoverage ||
        (shipRoute && isInternational(shipRoute)
          ? option.defaultInternational
          : option.defaultDomestic),
    )

  const getPackagingTypes: (productId: string) => ProductLinePackagingType[] = (
    productId: string,
  ) =>
    getOptions<ProductLinePackagingType>(productId, PACKAGING_FIELD).map((packaging) => ({
      ...packaging,
      display: packaging.name !== DEFAULT_PACKAGING_TYPE_NAME,
    }))

  const getDefaultPackagingType: (
    options: ProductLinePackagingType[],
  ) => ProductLinePackagingType | undefined = (options) =>
    options.find((option) => option.default === true)

  return {
    getOptions,
    getCoverageOptions,
    getPackagingTypes,
    coverageOptions,
    packagingTypes,
    isPending: isLoading,
    error: error || undefined,
    getDefaultCoverageOption,
    getDefaultPackagingType,
  }
}

export default useProductLineOptions
